import axios from 'axios';
import Cookies from 'js-cookie';
import { BACKEND_API_URL } from '../utils/api';
import { DEFAULT_SORT_FIELDS } from '../constants/canvasSort';
import { executeSearch } from './search.service';

const getAuthHeaders = () => ({
    Authorization: `Token ${Cookies.get('access_token')}`,
    'Content-Type': 'application/json',
});

// ============ Canvas API (New Consolidated Endpoint) ============

/**
 * Fetch canvas data - hierarchical items organized by depth
 *
 * @param {Object} params
 * @param {number|null} params.folderId - Target folder ID (null for root)
 * @param {string} params.archiveStatus - "all" | "excludeArchive" | "onlyArchive"
 * @param {string[]} params.sortFields - Array of sort fields in priority order
 *   Valid fields: assignee, star_level, status, created_at, updated_at
 * @param {string} params.sortOrder - "asc" | "desc"
 * @returns {Promise<{items: Object, selected_path: number[], current_folder: Object|null}>}
 */
export const fetchCanvasData = async ({ folderId = null, archiveStatus = 'excludeArchive', sortFields = DEFAULT_SORT_FIELDS, sortOrder = 'desc' }) => {
    const params = new URLSearchParams();

    if (folderId) {
        params.append('folder_id', folderId);
    }
    params.append('archive_status', archiveStatus);
    // Send sort fields as comma-separated string
    params.append('sort_fields', sortFields.join(','));
    params.append('sort_order', sortOrder);

    const response = await axios.get(
        `${BACKEND_API_URL}/api/canvas/?${params.toString()}`,
        { headers: getAuthHeaders() }
    );

    return response.data;
};

// ============ Users & Tags ============

/**
 * Fetch users and tags for bulk assign and other operations
 */
export const fetchUsersAndTags = async () => {
    const response = await axios.get(`${BACKEND_API_URL}/api/users_and_tags`, {
        headers: getAuthHeaders(),
    });
    return response.data;
};

/**
 * Update archive status for a single item
 */
export const updateItemArchiveStatus = async (itemId, newStatus) => {
    const response = await axios.get(
        `${BACKEND_API_URL}/api/item_archive_status_change/${itemId}?status=${newStatus}`,
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Batch move items to a destination folder
 */
export const batchMoveItems = async (itemIds, destinationId) => {
    const response = await axios.post(
        `${BACKEND_API_URL}/api/batch_move_items/`,
        { item_ids: itemIds, destination_id: destinationId },
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Batch create shortcuts for items
 */
export const batchCreateShortcuts = async (originalItemIds, destinationId) => {
    const response = await axios.post(
        `${BACKEND_API_URL}/api/batch_create_shortcuts/`,
        { original_item_ids: originalItemIds, destination_id: destinationId },
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Bulk assign items to an assignee
 */
export const bulkAssignItems = async (itemIds, assigneeId) => {
    const response = await axios.post(
        `${BACKEND_API_URL}/api/bulk_assign/`,
        { item_ids: itemIds, assignee_id: assigneeId },
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Update assignee for a folder
 */
export const updateFolderAssignee = async (folderId, assigneeId, isDelete = false) => {
    const response = await axios.post(
        `${BACKEND_API_URL}/api/folder/update_assignee/${folderId}`,
        { assignee: assigneeId, is_delete: isDelete },
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Update assignee for a link
 */
export const updateLinkAssignee = async (linkId, assigneeId, isDelete = false) => {
    const response = await axios.post(
        `${BACKEND_API_URL}/api/link/update_assignee/${linkId}`,
        { assignee: assigneeId, is_delete: isDelete },
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Create a new folder
 */
export const createFolder = async (name, parentFolderId = null) => {
    const trimmedName = name?.trim();
    if (!trimmedName) {
        throw new Error('Folder name cannot be empty');
    }

    const url = parentFolderId
        ? `${BACKEND_API_URL}/api/folder/create/${parentFolderId}`
        : `${BACKEND_API_URL}/api/folder/create/`;

    const response = await axios.post(url, { name: trimmedName }, { headers: getAuthHeaders() });
    return response.data;
};

/**
 * Add comment to an item (used when pasting channel messages)
 */
export const addItemComment = async (itemId, formData) => {
    const response = await axios.post(
        `${BACKEND_API_URL}/api/item/comment/add/${itemId}`,
        formData,
        {
            headers: {
                Authorization: `Token ${Cookies.get('access_token')}`,
                'Content-Type': 'multipart/form-data',
            },
        }
    );
    return response.data;
};

/**
 * Toggle star level for an item
 */
export const toggleStarLevel = async (itemId) => {
    const response = await axios.get(
        `${BACKEND_API_URL}/api/item_starred_value_change/${itemId}`,
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Bulk create multiple folders in a single request
 * @param {Array} folders - Array of folder definitions with name, optional temp_id, parent_id, or parent_temp_id
 * @param {number|null} parentId - Default parent ID for folders without explicit parent
 * @returns {Object} Response with success_count and created folders array
 *
 * Folder format:
 * - { name: "Folder Name" } - Creates at root or under parentId
 * - { name: "Folder", temp_id: "t1" } - Creates folder and allows referencing it as parent
 * - { name: "Child", parent_temp_id: "t1" } - Creates as child of folder with temp_id "t1"
 * - { name: "Child", parent_id: 123 } - Creates as child of existing folder with ID 123
 */
export const bulkCreateFolders = async (folders, parentId = null) => {
    const validFolders = folders.filter(f => f.name?.trim());
    if (validFolders.length === 0) {
        throw new Error('No valid folder names provided');
    }

    const response = await axios.post(
        `${BACKEND_API_URL}/api/folders/bulk_create/`,
        { folders: validFolders, parent_id: parentId },
        { headers: getAuthHeaders() }
    );
    return response.data;
};

// ============ Delete Operations ============

/**
 * Delete a folder
 */
export const deleteFolder = async (folderId) => {
    const response = await axios.get(
        `${BACKEND_API_URL}/api/folder/delete/${folderId}`,
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Delete a link
 */
export const deleteLink = async (linkId) => {
    const response = await axios.get(
        `${BACKEND_API_URL}/api/link/delete/${linkId}`,
        { headers: getAuthHeaders() }
    );
    return response.data;
};

// ============ Link Operations ============

/**
 * Create a new link
 */
export const createLink = async (link, name, parentFolderId = null) => {
    const url = parentFolderId
        ? `${BACKEND_API_URL}/api/link/create/${parentFolderId}`
        : `${BACKEND_API_URL}/api/link/create/`;

    const response = await axios.post(url, { link, name }, { headers: getAuthHeaders() });
    return response.data;
};

/**
 * Fetch children of a folder (folders, links, shortcuts)
 * @param {number} folderId - Folder ID to fetch children for
 * @param {string} archiveStatus - "all" | "excludeArchive" | "onlyArchive"
 * @returns {Promise<{folders: Array, links: Array, shortcuts: Array}>}
 */
export const fetchFolderChildren = async (folderId, archiveStatus = 'excludeArchive') => {
    const params = new URLSearchParams();
    if (archiveStatus) {
        params.append('archive_status', archiveStatus);
    }

    const url = `${BACKEND_API_URL}/api/folders/${folderId}/${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await axios.get(url, { headers: getAuthHeaders() });
    return response.data;
};

/**
 * Create a new Google Sheet or Doc
 * @param {string} name - Name of the document
 * @param {boolean} isSheet - true for sheet, false for doc
 * @returns {Promise<string>} URL of the created document
 */
export const createGoogleDocument = async (name, isSheet) => {
    const response = await axios.post(
        `${BACKEND_API_URL}/api/google/create_new_document/`,
        { name, is_sheet: isSheet },
        { headers: getAuthHeaders() }
    );
    const sheetId = response.data.id;
    return isSheet
        ? `https://docs.google.com/spreadsheets/d/${sheetId}`
        : `https://docs.google.com/document/d/${sheetId}`;
};

// ============ Bookmark View Operations ============

const newestFirst = (a, b) => new Date(b.created_at) - new Date(a.created_at);

// Move the given user to the front of a folder's assignees, so rows show them first ("You +2")
const withAssigneeFirst = (folder, userId) => {
    const employees = folder.employees || [];
    const index = employees.findIndex((employee) => String(employee.id) === String(userId));
    if (index <= 0) return folder;
    return { ...folder, employees: [employees[index], ...employees.filter((_, i) => i !== index)] };
};

/**
 * Fetch all bookmarked folders for the current user
 * @returns {Promise<Array>} Array of bookmarked folder items
 */
export const fetchBookmarkedFolders = async () => {
    const data = await executeSearch({ bookmarkedOnly: true }, 'folders');
    return data.folders || [];
};

/**
 * Fetch all folders assigned to a user, newest first, with that user listed first among assignees
 * @param {number} userId - Assignee user ID
 * @param {{dateFrom, dateTo}} dateRange - Optional folder creation date range ('yyyy-MM-dd')
 * @returns {Promise<Array>} Array of assigned folder items
 */
export const fetchAssignedFolders = async (userId, dateRange = {}) => {
    const data = await executeSearch({ assignee: userId, ...dateRange }, 'folders');
    return (data.folders || [])
        .map((folder) => withAssigneeFirst(folder, userId))
        .sort(newestFirst);
};

/**
 * Fetch channel messages and canvas comments in which a user is @mentioned
 * @param {number} userId - Mentioned user ID
 * @param {{dateFrom, dateTo}} dateRange - Optional message date range ('yyyy-MM-dd')
 * @returns {Promise<Array>} Messages sorted newest first (source_type: 'channel' | 'canvas')
 */
export const fetchMentionedMessages = async (userId, dateRange = {}) => {
    const data = await executeSearch({ sentTo: userId, mentionsOnly: true, ...dateRange }, 'messages');
    return data.messages || [];
};

// ============ Unread Count Operations ============

/**
 * Fetch unread counts for all direct children of a folder
 * @param {number|null} folderId - Folder ID (null for root level)
 * @returns {Promise<Object>} { item_id: { unread_count, own_unread_count }, ... }
 */
export const fetchUnreadCounts = async (folderId = null) => {
    const url = folderId
        ? `${BACKEND_API_URL}/api/unread_counts/${folderId}/`
        : `${BACKEND_API_URL}/api/unread_counts/`;
    const response = await axios.get(url, { headers: getAuthHeaders() });
    // Returns: { item_id: { unread_count, own_unread_count }, ... }
    return response.data;
};

/**
 * Mark all comments as read for an item
 * Sets user's read cursor to current comment_seq
 * @param {number} itemId - Item ID to mark as read
 * @returns {Promise<Object>} { status: "ok" }
 */
export const markAllRead = async (itemId) => {
    const response = await axios.post(
        `${BACKEND_API_URL}/api/mark_all_read/${itemId}/`,
        {},
        { headers: getAuthHeaders() }
    );
    return response.data;
};
